import PropTypes from 'prop-types';
import Betslip from './betslip.js';
import MojitoCore from 'mojito/core';
import { noop } from 'mojito/utils';

const UIView = MojitoCore.Presentation.UIView;

/**
 * Betslip view displaying betslip data.
 *
 * @class Betslip
 * @private
 * @memberof Mojito.Modules.Betslip.BetslipView
 */

/**
 * Betslip prop types.
 *
 * @property {Mojito.Services.Betslip.types.Betslip} [betslip] - Betslip object.
 * @property {Function} [scrollFullscreenBetslipToBottom = ()=>{}] - Function that scrolls the fullscreen betslip content to bottom.
 * @property {string} [currencyCode] - Currency code to use in betslip.
 * @property {Mojito.Services.Bonus.types.FreeBet[]} [freeBets] - List of free bets to display.
 * @property {boolean} [betPlacementButtonDisabled] - Bet placement button state override.
 * @property {Mojito.Services.Betslip.types.STAKE_GROUP_NAME} [stakeGroup] - Stake group.
 * @property {boolean} [hasOddsChangeBet = false] - Flag to indicate if betslip contains any unaccepted odds changes.
 * @property {boolean} [hasHcapChangeBet = false] - Flag to indicate if betslip contains any unaccepted handicap changes.
 * @property {boolean} [hasBoostedHcapChange = false] - Flag to indicate if betslip contains any unaccepted boosted handicap changes.
 * @property {boolean} [isSuspended = false] - Flag to suspend place bet content.
 * @property {boolean} [isExpired = false] - Flag to disable bet content due to bet expiration.
 * @property {Mojito.Services.Betslip.types.BETSLIP_STATUS} [betslipStatus] - Betslip status.
 * @property {Array<Mojito.Services.Betslip.types.Error>} [validationErrors] - List of betslip validation errors.
 * @property {Mojito.Services.Betslip.types.COMMIT_BETSLIP_MODE} [commitBetslipMode] - Commit betslip mode, indicating whether user can place a bet or save for later placement in a shop.
 * @property {Function} [onAcceptTechnicalError] - Callback is getting triggered once user accepts unexpected technical error.
 * @property {node} [children] - Child components.
 * @property {object} scrollPaneProps - These scroll pane props are needed to ensure correct scrolling when a user interacts with the mobile betslip.
 *
 * @memberof Mojito.Modules.Betslip.BetslipView.Betslip
 */
const propTypes = {
    betslip: PropTypes.object,
    scrollFullscreenBetslipToBottom: PropTypes.func,
    currencyCode: PropTypes.string,
    freeBets: PropTypes.array,
    betPlacementButtonDisabled: PropTypes.bool,
    stakeGroup: PropTypes.string,
    hasOddsChangeBet: PropTypes.bool,
    hasHcapChangeBet: PropTypes.bool,
    hasBoostedHcapChange: PropTypes.bool,
    isSuspended: PropTypes.bool,
    isExpired: PropTypes.bool,
    betslipStatus: PropTypes.string,
    validationErrors: PropTypes.array,
    children: PropTypes.node,
    onAcceptTechnicalError: PropTypes.func,
    commitBetslipMode: PropTypes.string,
    scrollPaneProps: PropTypes.object.isRequired,
};

const defaultProps = {
    scrollFullscreenBetslipToBottom: noop,
    hasOddsChangeBet: false,
    hasHcapChangeBet: false,
    hasBoostedHcapChange: false,
    isSuspended: false,
    isExpired: false,
};

export default UIView('Betslip', Betslip, propTypes, defaultProps);
